import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import omit from 'lodash/omit';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import Button from 'chameleon/ui-stack/ui/base/button';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import {
  AssignPseudoAction,
  DuplicatePseudoAction,
  EditPseudoAction,
  SharePseudoAction,
} from 'chameleon/ui-stack/ui/assessments/actions/phanthom-actions';
import { ASSESSMENT_BUTTONS } from 'legacy/src/constants/assessmentPreviewButtons';
import ScoringGuideAction from 'legacy/src/utility/AssessmentActions/actions/ScoringGuideAction';
import PrintPreviewAction from 'legacy/src/utility/AssessmentActions/actions/PrintPreviewAction';
import ViewQuizSummaryAction from 'legacy/src/utility/AssessmentActions/actions/ViewQuizSummaryAction';
import DuplicateQuizAction from 'legacy/src/utility/AssessmentActions/actions/DuplicateQuizAction';
import AssignAction from 'legacy/src/utility/AssessmentActions/actions/AssignAction';
import RemoveQuestionAction from 'legacy/src/utility/AssessmentActions/actions/RemoveQuestionAction';
import EditAction from 'legacy/src/utility/AssessmentActions/actions/EditAction';
import AddQuestionsAction from 'legacy/src/utility/AssessmentActions/actions/AddQuestionsAction';
import ReorderQuestionsAction from 'legacy/src/utility/AssessmentActions/actions/ReorderQuestionsAction';
import EditModeAction from 'legacy/src/utility/AssessmentActions/actions/EditModeAction';
import CopyQuestionAction from 'legacy/src/utility/AssessmentActions/actions/CopyQuestionAction';
import SharedAssessmentActions from 'legacy-src/utility/AssessmentActions/actions/SharedAssessmentActions';

import { LoadingSpinner } from 'legacy-packages/ft-cb';
import ScoringWorksheetAction from 'chameleon/legacy/src/utility/AssessmentActions/actions/ScoringWorksheetAction';

const LABELS = {
  SCORING_GUIDELINES: 'Scoring Guideline',
  SCORING_WORKSHEET: 'Scoring Worksheet',
  PRINT_PREVIEW: 'Print Preview',
  DUPLICATE: 'Duplicate Quiz',
  ASSIGN: 'Assign Quiz',
  EDIT_MODE: 'Edit Quiz',
  VIEW_QUIZ_SUMMARY: 'View Quiz Summary',
};

const EDIT_LABELS = {
  ADD: 'Add Question to Quiz',
  REMOVE: 'Remove Question from Quiz',
  REORDER: 'Reorder Questions',
  DUPLICATE: 'Duplicate Question',
  EDIT: 'Edit Question',
};

/**
 * Builds the button with a list of actions
 * */
const AssessmentActions = (props) => {
  const popover = useRef(null);
  const {
    visibleActions,
    subjectInfo,
    assignment,
    onDeleteClick,
    onEditQuestionClick,
    showEditQuestion,
    showReorderQuestions,
    onReorderQuestionsClick,
    onAssign,
    setIsEditModeOn,
    isEditModeOn,
    isEditModeDataLoading,
    router,
    previewEditMode,
    showCopyQuestion,
    onCopyQuestionClick,
    newQuestionItemId,
    isAdminOrCoordinator,
    renderSharedAssessmentActions,
    isComingfromSharedAssessmentPage,
    sharedAssessmentInfo,
    enableSharedAssessments,
    actionsTriggerClassName,
  } = props;
  const {
    id: assessmentId,
    isPracticeExam,
    isOwner,
    isInCoTaughtClass: isCoTaught,
    is_ppc: isPPC,
  } = assignment;
  const { subject: subjectId } = router && router.params;

  const associatedResources =
    assignment.associatedResources &&
    assignment.associatedResources.reduce((acc, current) => {
      acc[current.resourceType] = current.resourceUrl;
      return acc;
    }, {});

  const onEditModeClick = () => {
    !isAdminOrCoordinator && setIsEditModeOn(true);
  };

  const onFinishEditing = () => {
    setIsEditModeOn(false);
    let query = get(router, ['location', 'query'], {});
    const pathname = get(router, ['location', 'pathname'], '');
    const excludeQueryParams = [previewEditMode, newQuestionItemId, 'item'];
    const hasQueryParamsToExclude = Object.keys(query).some((param) =>
      excludeQueryParams.includes(param),
    );

    if (hasQueryParamsToExclude) {
      query = omit(query, excludeQueryParams);
    }
    query.editMode = false;
    router.push({
      pathname,
      query,
    });
  };

  const canEditModeQuiz = !isPracticeExam;
  const editQuizAvailableButtons = [
    showEditQuestion,
    visibleActions[ASSESSMENT_BUTTONS.remove],
    visibleActions[ASSESSMENT_BUTTONS.edit],
    showReorderQuestions &&
      visibleActions[ASSESSMENT_BUTTONS.showReorderQuestions],
    visibleActions[ASSESSMENT_BUTTONS.copyQuestion],
  ];

  const assesmentType = {
    isPPC,
    isQB: isOwner,
    isPracticeExam,
  };

  const isEditQuizAvailable =
    !isPPC &&
    canEditModeQuiz &&
    editQuizAvailableButtons.some((status) => status);

  const popoverLabel = (
    <Button
      isPrimary
      className={actionsTriggerClassName || 'hover:bg-blue-800'}
    >
      Actions
    </Button>
  );

  const actionButtonClassName =
    'text-gray-800 mx-2 p-4 rounded-full hover:text-gray-800 hover:bg-gray-200';

  const ActionWithTooltip = ({ children, text, target }) => (
    <TextWithTooltip
      target={`${assessmentId}-${target}`}
      tooltip={text}
      ariaLabel={text}
      position="bottom"
    >
      {children}
    </TextWithTooltip>
  );

  if (renderSharedAssessmentActions)
    return (
      <Fragment>
        <PopOver
          ref={popover}
          contentClassName="p-0 rounded-2xl overflow-visible"
          className="z-10"
          trigger={popoverLabel}
        >
          <div className="AssessmentActions--actions-list">
            <SharedAssessmentActions
              {...sharedAssessmentInfo}
              isComingfromSharedAssessmentPage={
                isComingfromSharedAssessmentPage
              }
            />
          </div>
        </PopOver>
      </Fragment>
    );

  return (
    <Fragment>
      {isEditModeOn && (
        <Fragment>
          <div
            className="flex items-center gap-x-3 mr-3 edit-mode-container"
            data-test-id="inline-icons-container"
          >
            {isEditModeDataLoading && <LoadingSpinner />}
            {visibleActions[ASSESSMENT_BUTTONS.remove] && (
              <ActionWithTooltip
                text={EDIT_LABELS.REMOVE}
                target="remove-question"
              >
                <RemoveQuestionAction
                  ariaLabel={EDIT_LABELS.REMOVE}
                  onDeleteClick={onDeleteClick}
                  disabled={isEditModeDataLoading}
                />
              </ActionWithTooltip>
            )}
            {visibleActions[ASSESSMENT_BUTTONS.addQuestions] && (
              <ActionWithTooltip text={EDIT_LABELS.ADD} target="add-question">
                <AddQuestionsAction
                  router={router}
                  assessmentId={assessmentId}
                  masterSubjectId={subjectId}
                  label={EDIT_LABELS.ADD}
                  ref={(node) => node && node.focus()}
                  disabled={isEditModeDataLoading}
                />
              </ActionWithTooltip>
            )}
            {showEditQuestion && (
              <ActionWithTooltip text={EDIT_LABELS.EDIT} target="edit-question">
                <EditAction
                  title={EDIT_LABELS.EDIT}
                  arialLabel={EDIT_LABELS.EDIT}
                  className={actionButtonClassName}
                  onClick={onEditQuestionClick}
                  small
                  disabled={isEditModeDataLoading}
                />
              </ActionWithTooltip>
            )}
            {showCopyQuestion && (
              <ActionWithTooltip
                text={EDIT_LABELS.DUPLICATE}
                target="copy-question"
              >
                <CopyQuestionAction
                  arialLabel={EDIT_LABELS.DUPLICATE}
                  onClick={onCopyQuestionClick}
                  disabled={isEditModeDataLoading}
                />
              </ActionWithTooltip>
            )}
            {showReorderQuestions &&
              visibleActions[ASSESSMENT_BUTTONS.reorder] && (
                <ActionWithTooltip
                  text={EDIT_LABELS.REORDER}
                  target="reorder-questions"
                >
                  <ReorderQuestionsAction
                    ariaLabel={EDIT_LABELS.REORDER}
                    onClick={onReorderQuestionsClick}
                    disabled={isEditModeDataLoading}
                  />
                </ActionWithTooltip>
              )}
          </div>
          <Button
            data-test-id="action-finish-editing"
            onClick={onFinishEditing}
            isPrimary
            className={actionsTriggerClassName}
          >
            Finish Editing
          </Button>
        </Fragment>
      )}
      {!isEditModeOn && (
        <PopOver
          ref={popover}
          contentClassName="p-0 rounded-2xl overflow-visible"
          className="z-10"
          trigger={popoverLabel}
        >
          <div className="AssessmentActions--actions-list">
            {visibleActions[ASSESSMENT_BUTTONS.assign] && !isCoTaught && (
              <div data-test-id="action-assign">
                <AssignAction
                  role="link"
                  tagName="div"
                  label={LABELS.ASSIGN}
                  subjectInfo={subjectInfo}
                  assessment={assignment}
                  onAssign={onAssign}
                />
              </div>
            )}
            {isCoTaught && <AssignPseudoAction />}
            {visibleActions[ASSESSMENT_BUTTONS.copy] && !isCoTaught && (
              <DuplicateQuizAction
                role="link"
                tagName="div"
                assessmentId={assessmentId}
                label={LABELS.DUPLICATE}
              />
            )}
            {isCoTaught && <DuplicatePseudoAction />}
            {isEditQuizAvailable && !isCoTaught && (
              <EditModeAction
                assignment={assignment}
                masterSubjectId={subjectId}
                onClick={onEditModeClick}
                label={LABELS.EDIT_MODE}
              />
            )}
            {isCoTaught && <EditPseudoAction />}
            {isCoTaught && <SharePseudoAction />}
            {visibleActions[ASSESSMENT_BUTTONS.printPreview] && (
              <PrintPreviewAction
                role="link"
                assessmentId={assessmentId}
                associatedResources={associatedResources}
                label={LABELS.PRINT_PREVIEW}
                assesmentType={assesmentType}
              />
            )}
            {visibleActions[ASSESSMENT_BUTTONS.scoringGuide] && (
              <ScoringGuideAction
                role="link"
                assessmentId={assessmentId}
                associatedResources={associatedResources}
                label={LABELS.SCORING_GUIDELINES}
                assesmentType={assesmentType}
              />
            )}
            {visibleActions[ASSESSMENT_BUTTONS.scoringWorksheet] &&
              associatedResources.SCORING_WORKSHEET && (
                <ScoringWorksheetAction
                  role="link"
                  assessmentId={assessmentId}
                  associatedResources={associatedResources}
                  label={LABELS.SCORING_WORKSHEET}
                  assesmentType={assesmentType}
                />
              )}
            {enableSharedAssessments &&
              visibleActions[ASSESSMENT_BUTTONS.viewQuizSummary] && (
                <ViewQuizSummaryAction
                  role="link"
                  quizSummaryId={assessmentId}
                  label={LABELS.VIEW_QUIZ_SUMMARY}
                  subjectId={subjectId}
                />
              )}
          </div>
        </PopOver>
      )}
    </Fragment>
  );
};

AssessmentActions.propTypes = {
  visibleActions: PropTypes.shape({
    reorder: PropTypes.bool,
    remove: PropTypes.bool,
    printPreview: PropTypes.bool,
    scoringGuide: PropTypes.bool,
    assign: PropTypes.bool,
    copy: PropTypes.bool,
    edit: PropTypes.bool,
    viewQuizSummary: PropTypes.bool,
  }),
  showEditQuestion: PropTypes.bool,
  showReorderQuestions: PropTypes.bool,
  subjectInfo: PropTypes.object.isRequired,
  assignment: PropTypes.object.isRequired,
  onDeleteClick: PropTypes.func,
  onEditQuestionClick: PropTypes.func,
  onReorderQuestionsClick: PropTypes.func,
  onAssign: PropTypes.func,
  groups: PropTypes.array.isRequired,
  setIsEditModeOn: PropTypes.func.isRequired,
  isEditModeOn: PropTypes.bool.isRequired,
  previewEditMode: PropTypes.string.isRequired,
  showCopyQuestion: PropTypes.bool,
  onCopyQuestionClick: PropTypes.func,
  newQuestionItemId: PropTypes.string,
  isAdminOrCoordinator: PropTypes.bool,
  renderSharedAssessmentActions: PropTypes.bool,
  isComingfromSharedAssessmentPage: PropTypes.bool,
  sharedAssessmentInfo: PropTypes.shape({
    shareKey: PropTypes.string,
    itemId: PropTypes.number,
    itemShareId: PropTypes.string,
    assessmentCopyName: PropTypes.string,
  }),
  actionsTriggerClassName: PropTypes.string,
};

AssessmentActions.defaultProps = {
  visibleActions: {
    reorder: false,
    remove: false,
    printPreview: false,
    scoringGuide: false,
    assign: false,
    copy: false,
    edit: false,
    viewQuizSummary: false,
  },
  showEditQuestion: false,
  showReorderQuestions: false,
  onDeleteClick: () => {},
  onEditQuestionClick: () => {},
  onReorderQuestionsClick: () => {},
  onAssign: () => {},
  showCopyQuestion: false,
  onCopyQuestionClick: () => {},
  newQuestionItemId: '',
  isAdminOrCoordinator: false,
  renderSharedAssessmentActions: false,
  isComingfromSharedAssessmentPage: false,
  sharedAssessmentInfo: {},
  actionsTriggerClassName: '',
};

export default AssessmentActions;
